import { toLabelValue } from '@/common/convertToDropdownOptions';
import { DropdownOption } from '@/common/getDropdownList';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDynamicLocale } from '@/hoslog-intl';
import { useMutation } from '@/react-query/hooks/useMutation/useMutation';
import { rolesQueries } from '@/services/rolesPrivilege/roles.queries';
import { USERAGENT } from '@/services/rolesPrivilege/roles.types';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

const assignRoleModalSchema = z.object({
  userType: z.string().min(1, { message: 'Please select a user type' }),
  user: z.string().min(1, { message: 'Please enter a user' }),
  currentRole: z.string().optional(),
  newRole: z.string().min(1, { message: 'Please select a new role' }),
});

type AssignNewRoleFormProps = {
  activeTab: string;
  agents: USERAGENT[];
  availableRoles: DropdownOption[];
  roleType: DropdownOption[];
};

export const AssignNewRoleForm = (props: AssignNewRoleFormProps) => {
  const form = useForm({
    schema: assignRoleModalSchema,
    mode: 'onBlur',
    defaultValues: {
      userType: props.roleType[0].value,
      user: '',
      currentRole: '',
      newRole: '',
    },
  });

  const currentRole =
    props.agents.find((agent) => {
      return agent.agentId === form.watch('user');
    })?.roleName || '';

  const roleOptions = toLabelValue(props.availableRoles ?? [], {
    labelKey: 'label',
    valueKey: 'value',
  });

  const userAgentOptions = toLabelValue(props.agents, {
    labelKey: 'userName',
    valueKey: 'agentId',
  });

  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation('assignRole', {
    onSuccess: (res) => {
      toast.success(res.message);
      void queryClient.invalidateQueries({
        queryKey: rolesQueries.all(),
      });
    },
    onError: (res) => {
      toast.error(res.message);
    },
  });
  const { t } = useDynamicLocale();

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        await mutateAsync({
          roleId: data.newRole,
          roleType: props.roleType[0].value || '',
          agentId: data.user,
        });
      }}
    >
      <div className="flex flex-col gap-4.5">
        <div>
          <Select
            disabled
            label="rolesPrivileges.assignNewRoleModal.userType"
            {...form.register('userType')}
            placeholder={t('common.form.placeholder.select')}
            selected={props.roleType[0].value}
            options={props.roleType}
          />
        </div>
        <div>
          <Select
            label="rolesPrivileges.assignNewRoleModal.user"
            {...form.register('user')}
            placeholder={t('common.form.placeholder.select')}
            options={userAgentOptions}
          />
        </div>

        <Input
          disabled
          label="rolesPrivileges.assignNewRoleModal.currentRole"
          placeholder={t('rolesPrivileges.assignNewRoleModal.currentRole')}
          {...form.register('currentRole')}
          value={currentRole}
        />
        <div>
          <Select
            label="rolesPrivileges.assignNewRoleModal.newRole"
            {...form.register('newRole')}
            placeholder={t('common.form.placeholder.select')}
            options={roleOptions}
          />
        </div>
      </div>
    </Form>
  );
};
